import { Link } from 'react-router-dom';
import {
  displayInitialsForShift,
  formatInitialsForChip,
  formatShiftRangeForChip,
  shiftHalf,
} from '@/lib/scheduling';
import type {
  DayAnnotation,
  JobType,
  Shift,
  TimeFormat,
} from '@/lib/types';

const ROLE_COLORS: Record<JobType, { bg: string; border: string; text: string }> = {
  pharmacist:          { bg: '#E6F1FB', border: '#185FA5', text: '#0C447C' },
  pharmacy_technician: { bg: '#FAEEDA', border: '#BA7517', text: '#633806' },
  pharmacy_assistant:  { bg: '#EAF3DE', border: '#639922', text: '#27500A' },
  student:             { bg: '#EEEDFE', border: '#534AB7', text: '#3C3489' },
  manager:             { bg: '#E6F1FB', border: '#185FA5', text: '#0C447C' },
  other:               { bg: '#F1EFE8', border: '#888780', text: '#2C2C2A' },
};

// Day-annotation tag palette. Tags are user-defined strings stored on
// `locations.day_annotation_tags`, not predefined types -- so we hash each
// tag name to one of these 10 print-safe pastels. The hash is deterministic
// (same tag string -> same color forever), case-insensitive, and trimmed,
// so "Audit", "audit", and " Audit " all resolve identically.
// See: docs/decisions/2026-05-11_print_density_redesign.md (v6 polish).
const TAG_COLORS: { bg: string; text: string; border: string }[] = [
  { bg: '#fef3c7', text: '#92400e', border: '#fde68a' }, // amber
  { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }, // blue
  { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' }, // red
  { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' }, // indigo
  { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' }, // green
  { bg: '#fce7f3', text: '#9f1239', border: '#fbcfe8' }, // pink
  { bg: '#cffafe', text: '#155e75', border: '#a5f3fc' }, // cyan
  { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' }, // orange
  { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff' }, // purple
  { bg: '#f1f5f9', text: '#334155', border: '#e2e8f0' }, // slate
];

// djb2 hash -- simple, fast, well-distributed for short strings.
function tagColorFor(tag: string): { bg: string; text: string; border: string } {
  const s = tag.trim().toLowerCase();
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash) + s.charCodeAt(i);
    hash = hash | 0; // force 32-bit
  }
  const idx = Math.abs(hash) % TAG_COLORS.length;
  return TAG_COLORS[idx];
}

const SLOT_COUNT = 5;

export interface DayCellProps {
  date: string;
  dayNumber: number;
  isOutOfMonth: boolean;
  isClosed: boolean;
  shifts: Shift[];
  staffJobTypes: Map<string, JobType>;
  annotation: DayAnnotation | null;
  shiftSplitTime: string;
  timeFormat: TimeFormat;
  scheduleStatus: 'draft' | 'published' | 'closed';
  locationId: string;
  yyyymm: string;
  canEdit: boolean;
  onAddShift: (date: string, half: 'am' | 'pm') => void;
  onClickShift: (shift: Shift) => void;
  onEditAnnotation: (date: string) => void;
  onDropStaff?: (date: string, half: 'am' | 'pm', staffId: string) => void;
  // 0107: intra-month day copy. Only fired when the cell has at least one
  // shift and the schedule is a draft the viewer can edit.
  onCopyDay?: (date: string) => void;
}

export function DayCell({
  date,
  dayNumber,
  isOutOfMonth,
  isClosed,
  shifts,
  staffJobTypes,
  annotation,
  shiftSplitTime,
  timeFormat,
  scheduleStatus,
  locationId,
  yyyymm,
  canEdit,
  onAddShift,
  onClickShift,
  onEditAnnotation,
  onDropStaff,
  onCopyDay,
}: DayCellProps) {
  const cellEditable = canEdit && !isClosed && !isOutOfMonth && scheduleStatus !== 'closed';
  const tagged = !!annotation && annotation.tags.length > 0;
  const activeShifts = shifts.filter((s) => !s.cancelled_at);
  const amShifts = activeShifts.filter((s) => shiftHalf(s.start_time, shiftSplitTime) === 'am').sort((a, b) => a.start_time.localeCompare(b.start_time));
  const pmShifts = activeShifts.filter((s) => shiftHalf(s.start_time, shiftSplitTime) === 'pm').sort((a, b) => a.start_time.localeCompare(b.start_time));
  // Copy affordance appears only on draft days that HAVE shifts and are
  // editable. It's absolutely-positioned inside the cell and hidden on
  // print via calendar-print-hide (see index.css).
  const showCopyBtn = cellEditable && scheduleStatus === 'draft' && activeShifts.length > 0 && !!onCopyDay;

  return (
    <div
      className={'calendar-cell calendar-cell-screen-h relative flex flex-col w-full bg-card border border-border ' + (isOutOfMonth ? 'opacity-60 ' : '') + (isClosed ? 'bg-muted/40 ' : '')}
    >
      {/* v6 (2026-05-11): added `calendar-day-header` class so the print
          CSS can switch this row to flex-direction:column, stacking the
          day number (12 px) above the tag-row (12 px) instead of having
          them side-by-side. On screen the layout stays horizontal. */}
      <div className="calendar-day-header flex items-start gap-2 px-2 pt-1.5">
        <Link
          to={`/schedule/${locationId}/${yyyymm}/day/${date}`}
          className={'calendar-day-number tabular-nums shrink-0 ' + (tagged ? 'text-amber-700 font-semibold text-base' : isOutOfMonth ? 'text-muted-foreground text-sm' : 'text-foreground font-medium text-sm')}
        >
          {dayNumber}
        </Link>
        <TagRow annotation={annotation} canEdit={cellEditable} onEdit={() => onEditAnnotation(date)} />
        {showCopyBtn && (
          <button
            type="button"
            onClick={() => onCopyDay!(date)}
            className="calendar-copy-day-btn calendar-add-affordance shrink-0 text-[10px] leading-none text-muted-foreground hover:text-primary rounded px-1 py-0.5 border border-transparent hover:border-border"
            title="Copy this day's shifts to other days in this month"
            aria-label="Copy day"
          >
            ⧉
          </button>
        )}
      </div>

      {isClosed ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-xs text-muted-foreground">Closed</span>
        </div>
      ) : (
        <div className="flex-1 flex px-1 pt-1">
          <HalfColumn shifts={amShifts} staffJobTypes={staffJobTypes} timeFormat={timeFormat} canEdit={cellEditable} onAdd={() => onAddShift(date, 'am')} onClickShift={onClickShift} morePath={`/schedule/${locationId}/${yyyymm}/day/${date}`} onDropStaff={onDropStaff ? (sid) => onDropStaff(date, 'am', sid) : undefined} />
          <div className="border-l border-border border-dashed mx-0.5" />
          <HalfColumn shifts={pmShifts} staffJobTypes={staffJobTypes} timeFormat={timeFormat} canEdit={cellEditable} onAdd={() => onAddShift(date, 'pm')} onClickShift={onClickShift} morePath={`/schedule/${locationId}/${yyyymm}/day/${date}`} onDropStaff={onDropStaff ? (sid) => onDropStaff(date, 'pm', sid) : undefined} />
        </div>
      )}

      <div
        className={'calendar-bottom-note border-t border-border bg-muted/40 px-2 py-1 ' + (annotation?.note ? 'calendar-has-note' : '')}
        data-note-len={noteLengthBucket(annotation?.note)}
      >
        {annotation?.note ? (
          <button type="button" onClick={() => cellEditable && onEditAnnotation(date)} className={'block w-full text-left text-[10px] truncate ' + (cellEditable ? 'cursor-pointer hover:underline' : 'cursor-default')} title={annotation.note}>
            {annotation.note}
          </button>
        ) : cellEditable ? (
          <button type="button" onClick={() => onEditAnnotation(date)} className="calendar-add-affordance block w-full text-left text-[10px] text-muted-foreground hover:text-foreground">+ Add note</button>
        ) : (
          <span className="block text-[10px] text-muted-foreground">&nbsp;</span>
        )}
      </div>
    </div>
  );
}

function TagRow({ annotation, canEdit, onEdit }: { annotation: DayAnnotation | null; canEdit: boolean; onEdit: () => void }) {
  const tags = annotation?.tags ?? [];
  // v6 (2026-05-11): every branch carries the .calendar-tag-row class so
  // the print CSS can reserve a 12 px row at the top of every cell --
  // including empty cells where the +Add affordance is hidden via
  // .calendar-add-affordance display:none. Without this, the shifts
  // area starts at different Y-coordinates across cells in the same
  // row, breaking the uniform grid look on paper.
  if (tags.length === 0) {
    if (!canEdit) return <div className="calendar-tag-row flex-1" aria-hidden="true" />;
    return (
      <button type="button" onClick={onEdit} className="calendar-tag-row calendar-add-affordance flex-1 text-left text-[10px] text-muted-foreground hover:text-foreground truncate">+ Add tag</button>
    );
  }
  // Render every tag chip inline. Non-last chips have `shrink-0` so they
  // keep their natural width; only the last chip is allowed to shrink and
  // truncate when the cell's top region runs out of horizontal room.
  // Container has `overflow-hidden` so anything still spilling past the
  // available width clips at the cell border instead of bleeding.
  return (
    <button type="button" onClick={canEdit ? onEdit : undefined} className={'calendar-tag-row flex-1 flex items-center gap-1 min-w-0 overflow-hidden ' + (canEdit ? 'cursor-pointer' : 'cursor-default')}>
      {tags.map((t, i) => {
        const isLast = i === tags.length - 1;
        // v6 polish (2026-05-12): deterministic hash -> 1 of 10 print-safe
        // pastels. Same tag string always gets the same color across the
        // whole month and across runs. See tagColorFor() above.
        const c = tagColorFor(t);
        return (
          <span
            key={t}
            className={'calendar-tag-chip text-[9px] font-medium px-1.5 py-0.5 rounded-full ' + (isLast ? 'truncate min-w-0' : 'shrink-0 whitespace-nowrap')}
            style={{ background: c.bg, color: c.text, borderColor: c.border }}
            title={t}
          >{t}</span>
        );
      })}
    </button>
  );
}

// Map a note's character length into a CSS class bucket used by @media print
// to step the font-size down so the note stays on a single line. Returning
// `undefined` keeps the default (8pt) for short notes. See decision log.
function noteLengthBucket(note: string | null | undefined): 'sm' | 'md' | 'lg' | undefined {
  if (!note) return undefined;
  const len = note.length;
  if (len <= 25) return undefined;
  if (len <= 33) return 'sm';
  if (len <= 45) return 'md';
  return 'lg';
}

function HalfColumn({ shifts, staffJobTypes, timeFormat, canEdit, onAdd, onClickShift, morePath, onDropStaff }: { shifts: Shift[]; staffJobTypes: Map<string, JobType>; timeFormat: TimeFormat; canEdit: boolean; onAdd: () => void; onClickShift: (shift: Shift) => void; morePath: string; onDropStaff?: (staffId: string) => void }) {
  const dropProps = canEdit && onDropStaff ? {
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes('text/x-pharmasync-staff')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    onDrop: (e: React.DragEvent) => {
      const sid = e.dataTransfer.getData('text/x-pharmasync-staff');
      if (sid) { e.preventDefault(); onDropStaff(sid); }
    },
  } : {};
  const visibleOnScreenCount = Math.min(shifts.length, SLOT_COUNT);
  const visibleOnScreen = shifts.slice(0, visibleOnScreenCount);
  const hiddenOnScreen = shifts.slice(visibleOnScreenCount);
  const overflow = hiddenOnScreen.length;
  const showPlus = canEdit && shifts.length <= SLOT_COUNT;

  return (
    <div className="flex-1 flex flex-col gap-0.5 min-w-0" {...dropProps}>
      {visibleOnScreen.map((s) => (
        <ShiftChip key={s.id} shift={s} jobType={s.staff_id ? staffJobTypes.get(s.staff_id) ?? 'other' : 'other'} timeFormat={timeFormat} onClick={() => onClickShift(s)} />
      ))}
      {hiddenOnScreen.map((s) => (
        <div key={s.id} className="calendar-shift-hidden-on-screen">
          <ShiftChip shift={s} jobType={s.staff_id ? staffJobTypes.get(s.staff_id) ?? 'other' : 'other'} timeFormat={timeFormat} onClick={() => onClickShift(s)} />
        </div>
      ))}
      {overflow > 0 && (
        <Link to={morePath} className="calendar-shift-more-link block text-[10px] text-center bg-muted/60 border border-border rounded px-1 py-0.5 hover:bg-muted text-foreground">+{overflow} more</Link>
      )}
      {showPlus && <PlusSlot onClick={onAdd} />}
    </div>
  );
}

function ShiftChip({ shift, jobType, timeFormat, onClick }: { shift: Shift; jobType: JobType; timeFormat: TimeFormat; onClick: () => void }) {
  const colors = ROLE_COLORS[jobType];
  // 0109: prefer the frozen snapshot on the shift; fall back to derived-from-
  // display-name when the snapshot is missing (older data). formatInitialsForChip
  // only auto-dots pure 2-letter values, so custom-set values like "SAK" or
  // "L-C" render exactly as the user typed them.
  const initials = displayInitialsForShift(shift);
  const initialsWithDots = formatInitialsForChip(initials);
  const isOpen = shift.is_open;
  const timeText = formatShiftRangeForChip(shift.start_time, shift.end_time, timeFormat);

  return (
    <button
      type="button"
      onClick={onClick}
      className={'calendar-shift-chip flex items-center w-full text-left text-[10px] font-medium px-1 py-0.5 rounded border tabular-nums overflow-hidden ' + (isOpen ? 'border-dashed bg-card text-muted-foreground' : '')}
      style={isOpen ? {} : { background: colors.bg, borderColor: colors.border, color: colors.text }}
      title={`${shift.staff_display_name_at_save ?? 'Open'} -- ${shift.start_time.slice(0,5)} to ${shift.end_time.slice(0,5)}${shift.override_reason ? ' (override)' : ''}`}
    >
      <span className="shrink-0">{isOpen ? 'Open' : initialsWithDots}</span>
      <span className="ml-auto whitespace-pre min-w-0 overflow-hidden text-ellipsis pl-1">
        {timeText.split('').map((c, i) =>
          c === 'M' ? <span key={i} className="ampm-m">M</span> : c
        )}
      </span>
      {shift.override_reason && <span className="ml-1 shrink-0">!</span>}
    </button>
  );
}

function PlusSlot({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="calendar-empty-slot calendar-add-affordance flex items-center justify-center text-[12px] text-muted-foreground border border-dashed border-border rounded hover:bg-muted/40 hover:text-foreground" style={{ height: 18 }} aria-label="Add shift">+</button>
  );
}
